import {
  Body,
  Box,
  Heading,
  Sheet,
  useBreakpointValue,
} from '@hover/blueprint';
import { AnimatePresence } from 'framer-motion';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';

import { useGetProjectQuery } from 'src/shared/graphql/generated';
import type {
  AiDisplayImage,
  Project as ProjectType,
} from 'src/shared/graphql/generated';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

import { Masonry } from '../Masonry';
import {
  SavedItem,
  SavedItemSkeleton,
} from '../Projects/Menu/AddSavedToProject';
import { getProjectImages } from '../Projects/Project/ProjectCarousel';

const MIN_IMAGES_FOR_EXPAND_GRID = 3;
const EXPANDED_GRID_COLUMNS = 4;
const COLLAPSED_GRID_COLUMNS = 3;

const SavedImageSheet = () => {
  const [numberOfColumns, setNumberOfColumns] = useState(3);

  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const { projectId } = useParams<{ projectId: string }>();
  const isMobile = useBreakpointValue({ base: true, tablet: false });

  const { data, loading } = useGetProjectQuery({
    variables: {
      projectId: Number(projectId),
    },
  });

  const project = data?.project as ProjectType | undefined;
  const addables: AiDisplayImage[] = useMemo(
    () => project?.addables || [],
    [project],
  );

  const images = useMemo(
    () => getProjectImages(addables).reverse() || [],
    [addables],
  );

  useEffect(() => {
    if (images.length > MIN_IMAGES_FOR_EXPAND_GRID) {
      setNumberOfColumns(EXPANDED_GRID_COLUMNS);
    } else {
      setNumberOfColumns(COLLAPSED_GRID_COLUMNS);
    }
  }, [images]);

  return (
    <Sheet.Contents
      contentKey="saved-images"
      header={
        <Box
          justifyContent={{ base: 'center', tablet: 'space-between' }}
          width="100%"
        >
          <Body
            display={{ base: 'flex', tablet: 'none' }}
            isSemiBold
            marginY="0px"
          >
            {isMobile
              ? t('projects.savedImages.mobileHeader')
              : t('projects.savedImages.header')}
          </Body>
          <Heading display={{ base: 'none', tablet: 'flex' }} size={700}>
            {t('projects.savedImages.header')}
          </Heading>
        </Box>
      }
      maxContentHeight={{ base: '100dvh', desktop: '70vh' }}
    >
      {loading ? (
        <Masonry
          columns={{ base: 2, tablet: COLLAPSED_GRID_COLUMNS }}
          fallbackRowHeight={{ base: 250, width250: 300, tablet: 400 }}
          gap={{ base: 300, tablet: 500 }}
          marginTop={300}
          role="list"
          width="100%"
        >
          <AnimatePresence>
            <SavedItemSkeleton numberOfColums={COLLAPSED_GRID_COLUMNS} />
            <SavedItemSkeleton numberOfColums={COLLAPSED_GRID_COLUMNS} />
            <SavedItemSkeleton numberOfColums={COLLAPSED_GRID_COLUMNS} />
          </AnimatePresence>
        </Masonry>
      ) : (
        <Masonry
          columns={{ base: 2, tablet: numberOfColumns }}
          fallbackRowHeight={{ base: 250, width250: 300, tablet: 400 }}
          gap={{ base: 300, tablet: 500 }}
          marginTop={300}
          role="list"
          width="100%"
        >
          <AnimatePresence>
            {images.map(image => {
              if (!image) return null;
              return (
                <SavedItem
                  displayImage={image.generatedImage}
                  editOnClick
                  key={image.generatedImage.id}
                  onClick={() => {}}
                />
              );
            })}
          </AnimatePresence>
        </Masonry>
      )}
    </Sheet.Contents>
  );
};

export { SavedImageSheet };
